(ns frontend.view.nev.kyc.process.flow-through-jurisdiction-editor
  (:require
   [clojure.data :as data]
   [common.localization :as loc]
   [common.schema.entities.workspace.kyc.kyc-utils :as kyc-utils]
   [frontend.utils :as fu]
   [frontend.state.current-session :as session]
   [frontend.view.modals.confirmation.view :as confirmation-modal]
   [frontend.view.modals.dynamic.view :as dynamic-modal]
   [frontend.view.modals.events :as modals]
   [frontend.view.nev.kyc.process-status-views.view-utils :as view-utils]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.radix-ui.tooltip.view :as tooltip]))

(defn get-jurisdiction-display-name
  "Get display name for a jurisdiction"
  [jurisdiction lang-kw]
  (kyc-utils/localize-display-string {} :kyc-flow-through-jurisdiction/jurisdiction (:jurisdiction jurisdiction) lang-kw))

(defn get-jurisdiction-summary-fields
  "Get the summary fields to display in the table"
  [jurisdiction schema lang-kw]
  [[:jurisdiction (kyc-utils/localize-display-string schema :kyc-flow-through-jurisdiction/jurisdiction (:kyc-flow-through-jurisdiction/jurisdiction jurisdiction) lang-kw)]
   [:ownership-percent (when-let [percent (:kyc-flow-through-jurisdiction/ownership-percent jurisdiction)] (fu/format-percentage percent))]
   [:taxable-percent (when-let [percent (:kyc-flow-through-jurisdiction/taxable-percent jurisdiction)] (fu/format-percentage percent))]
   [:tax-exempt-percent (when-let [percent (:kyc-flow-through-jurisdiction/tax-exempt-percent jurisdiction)] (fu/format-percentage percent))]
   [:is-transparent (when-let [is-transparent (:kyc-flow-through-jurisdiction/is-transparent jurisdiction)]
                      (kyc-utils/localize-display-string schema :kyc-flow-through-jurisdiction/is-transparent is-transparent lang-kw))]
   [:is-taxed-as-income-arises (when-let [is-taxed-as-income-arises (:kyc-flow-through-jurisdiction/is-taxed-as-income-arises jurisdiction)]
                                 (kyc-utils/localize-display-string schema :kyc-flow-through-jurisdiction/is-taxed-as-income-arises is-taxed-as-income-arises lang-kw))]])

(defn get-table-headers
  "Get table headers"
  [schema lang-kw]
  [""
   (kyc-utils/localize-question schema :kyc-flow-through-jurisdiction/jurisdiction lang-kw)
   (kyc-utils/localize-question schema :kyc-flow-through-jurisdiction/ownership-percent lang-kw)
   (kyc-utils/localize-question schema :kyc-flow-through-jurisdiction/taxable-percent lang-kw)
   (kyc-utils/localize-question schema :kyc-flow-through-jurisdiction/tax-exempt-percent lang-kw)
   (kyc-utils/localize-question schema :kyc-flow-through-jurisdiction/is-transparent lang-kw)
   (kyc-utils/localize-question schema :kyc-flow-through-jurisdiction/is-taxed-as-income-arises lang-kw)
   ""])

(defn open-jurisdiction-editor
  [{:keys [idx existing-jurisdiction kyc-entity-editor process-id state-atom lang-kw question-attribute
           question-meta answers-atom mandatory? read-only? save!]}]
  (let [kyc-process (session/get-entity process-id)
        jurisdiction-answers (r/atom (or existing-jurisdiction {}))
        field-errors-atom (r/atom {})
        jurisdictions (get @answers-atom question-attribute)
        jurisdiction-template-schema (:data/template question-meta)
        calc-new-jurisdiction-answer (fn []
                                       (if (>= idx (count jurisdictions))
                                         (concat jurisdictions
                                                 [(assoc @jurisdiction-answers
                                                         :ord
                                                         (count jurisdictions))])
                                         (->> jurisdictions
                                              (map-indexed (fn [uidx jurisdiction]
                                                             (assoc (if (= uidx idx)
                                                                      @jurisdiction-answers
                                                                      jurisdiction)
                                                                    :ord
                                                                    uidx)))
                                              (vec))))]
    (modals/open-modal!
     dynamic-modal/modal-key
     {:title               (if existing-jurisdiction
                             (loc/tr :edit-jurisdiction nil lang-kw)
                             (loc/tr :add-jurisdiction nil lang-kw))
      :render-fn           (fn [_state]
                             [:div
                              [kyc-entity-editor
                               {:container-id    (str "flow-through-jurisdiction-" idx)
                                :process-id      process-id
                                :state-atom      state-atom
                                :save!           identity
                                :mandatory?      mandatory?
                                :skip-info?      true
                                :read-only?      read-only?
                                :answers-atom    jurisdiction-answers
                                :field-errors-atom field-errors-atom
                                :template-schema jurisdiction-template-schema
                                :lang-kw         lang-kw}]])
      :show-button?        (not read-only?)
      :button-text         (loc/tr :save nil lang-kw)
      :custom-validator-fn (fn [_state]
                             (let [[old-only new-only _] (data/diff existing-jurisdiction @jurisdiction-answers)
                                   changes? (or (seq old-only)
                                                (seq new-only))]
                               (view-utils/modal-validator-tooltip
                                {:lang-kw           lang-kw
                                 :template-schema   jurisdiction-template-schema
                                 :answers-atom      jurisdiction-answers
                                 :field-errors-atom field-errors-atom
                                 :kyc-process       kyc-process
                                 :changes?          changes?})))
      :on-submit!          (fn [_]
                             (let [new-value (calc-new-jurisdiction-answer)]
                               (swap! answers-atom assoc question-attribute new-value)
                               (save!)))
      :on-before-close!    (fn [close!]
                             (if (= (get @answers-atom question-attribute) (calc-new-jurisdiction-answer))
                               (close!)
                               (modals/open-modal!
                                confirmation-modal/modal-key
                                {:body           (loc/tr :confirm-close-kyc-modal-without-saving nil lang-kw)
                                 :confirm-text   (loc/tr :close-without-saving nil lang-kw)
                                 :confirm-icon   ""
                                 :skip-checkbox? true
                                 :on-confirm     #(close!)})))})))

(defn view
  [{:keys [_kyc-entity-editor process-id _state-atom lang-kw question-attribute question-meta
           answers-atom mandatory? read-only? save!]
    :as   args}]
  (let [jurisdictions (get @answers-atom question-attribute)
        process (session/get-entity process-id)
        jurisdiction-schema (:data/template question-meta)
        wip-status? (-> process
                        :kyc-process/status
                        (= :kyc-process.status/wip))
        sorted-jurisdictions (sort-by :ord jurisdictions)
        jurisdiction-missing-data-indices
        (->> sorted-jurisdictions
             (map-indexed
              (fn [idx jurisdiction]
                (let [missing-fields (view-utils/resolve-missing-fields lang-kw
                                                                        jurisdiction-schema
                                                                        (atom jurisdiction)
                                                                        process)]
                  [idx missing-fields])))
             (filterv (fn [[_idx missing-fields]]
                        (seq missing-fields)))
             (mapv first)
             set)
        add-button-text (loc/tr :add-jurisdiction nil lang-kw)]
    [:div
     [:div.input-editor__label
      [:label.input-editor__label-text.pb-2
       {:class
        (when mandatory?
          "after:ml-0.5 after:text-danger-regular after:content-['*']")}
       (-> question-meta
           :translations
           (get lang-kw))]

      [:p
       {:class "text-sm m-0 font-medium text-base-light-text-paler mb-4"}
       (-> question-meta
           :info
           (get lang-kw))]]
     (if (and read-only?
              (empty? jurisdictions))
       [:div.preview
        [:span (loc/tr :no-jurisdictions-added nil lang-kw)]]
       [:div.max-w-100.hide-scrollbar
        [:table.zap-table.zap-table--striped.zap-table--has-footer.zap-table--width-auto
         [:thead.zap-table__headline
          [:tr.zap-table__row
           (->> (get-table-headers jurisdiction-schema lang-kw)
                (map-indexed
                 (fn [idx header]
                   [:th.zap-table__headline
                    {:key idx}
                    header])))]]
         [:tbody
          (->>
           sorted-jurisdictions
           (map-indexed
            (fn [idx jurisdiction]
              (let [summary-fields (get-jurisdiction-summary-fields jurisdiction jurisdiction-schema lang-kw)]
                [:tr
                 {:key (str "jurisdiction-" idx "-" (:ord jurisdiction))}
                 [:td.zap-table__cell
                  [:i.fal.fa-external-link.clickable
                   {:on-click (fn [_]
                                (open-jurisdiction-editor (assoc args
                                                                 :idx idx
                                                                 :existing-jurisdiction jurisdiction)))}]]
                 (map-indexed (fn [field-idx [_field-key field-value]]
                                [:td.zap-table__cell {:key field-idx}
                                 (or field-value "")])
                              summary-fields)
                 [:td.zap-table__cell
                  (when (and wip-status?
                             (seq jurisdiction-missing-data-indices))
                    {:style {:border-right "1px solid #D9D9DA"}})
                  (when-not read-only?
                    [:i.fal.fa-trash-alt.pointer
                     {:on-click (fn [_]
                                  (swap! answers-atom assoc
                                         question-attribute
                                         (concat (take idx jurisdictions)
                                                 (drop (inc idx) jurisdictions)))
                                  (save!))}])]

                 (when (and wip-status?
                            (contains? jurisdiction-missing-data-indices idx))
                   [:td
                    {:class "px-3 py-2"}
                    [tooltip/view
                     [:i {:class "fal fa-exclamation-triangle text-danger-regular"}]
                     [:span (loc/tr :data-missing nil lang-kw)]]])])))
           (doall))]
         (when-not read-only?
           [:tfoot
            [:tr.zap-table__row
             [:td.zap-table__footer-cell
              {:col-span (count (get-table-headers jurisdiction-schema lang-kw))}
              [button/primary-solid
               {:on-click (fn [_]
                            (let [idx (count jurisdictions)]
                              (open-jurisdiction-editor (assoc args
                                                               :idx idx))))}
               [:i.fal.fa-plus.pr-2]
               add-button-text]]]])]])]))
